import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  CircularProgress,
  TablePagination,
  useMediaQuery,
  Menu,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  DeleteSweep as DeleteAllIcon,
  FilterList as FilterIcon,
  CloudDownload as ExportIcon,
  RestartAlt as RestartIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  RadioButtonUnchecked as UncheckedIcon,
  RadioButtonChecked as CheckedIcon,
  Check as CheckIcon,
  Remove as MinusIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useHeartbeat } from '../../contexts/HeartbeatContext';
import SuccessDialog from '../../components/SuccessDialog';
import axios from 'axios';
import { getApiUrl } from '../../utils/apiUrl';
import './admin-css/UploadHistory-light.css';
import './admin-css/UploadHistory-dark.css';

type SortField = 'filename' | 'clientName' | 'uploadDate' | 'status' | 'recordCount';

interface UploadRecord {
  _id: string;
  clientId: string;
  clientName: string;
  clientUsername: string;
  fileName: string;
  recordCount: number;
  fileSize: string;
  version: number;
  uploadDate: string;
  uploadedBy: string;
  status: string;
}

const UploadHistory: React.FC = () => {
  const theme = useTheme();
  const { lastHeartbeat } = useHeartbeat();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [uploads, setUploads] = useState<UploadRecord[]>([]);
  const [filteredUploads, setFilteredUploads] = useState<UploadRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [showIndeterminate, setShowIndeterminate] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [selectedClientName, setSelectedClientName] = useState<string>('');
  
  // Mobile states
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedUploadForMenu, setSelectedUploadForMenu] = useState<UploadRecord | null>(null);
  
  // Filter and sort states
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState<Date | null>(null);
  const [dateToFilter, setDateToFilter] = useState<Date | null>(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Success Dialog state
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogConfig, setSuccessDialogConfig] = useState({
    title: '',
    message: '',
    autoCloseDelay: 3000,
  });
  
  // Sort states
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const fetchUploads = useCallback(async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      const response = await axios.get(
        `${getApiUrl()}/api/upload/admin/history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setUploads(response.data.data);
      }
    } catch (error: any) {
      setSuccessDialogConfig({
        title: 'Error Loading History',
        message: error.response?.data?.message || 'Failed to fetch upload history',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUploads();
  }, [fetchUploads]);

  // Auto-refresh uploads every 1 minute
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchUploads();
    }, 60000);

    return () => clearInterval(intervalId);
  }, [fetchUploads]);

  // Refresh when heartbeat updates
  useEffect(() => {
    if (lastHeartbeat) {
      fetchUploads();
    }
  }, [lastHeartbeat, fetchUploads]);

  // Filter and sort uploads
  useEffect(() => {
    let filtered = [...uploads];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(upload =>
        upload.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        upload.fileName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        upload.uploadedBy.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply date filter
    if (dateFromFilter || dateToFilter) {
      filtered = filtered.filter(upload => {
        const uploadDate = new Date(upload.uploadDate);
        
        if (dateFromFilter && dateToFilter) {
          // Both dates selected - filter between range
          const fromDate = new Date(dateFromFilter);
          fromDate.setHours(0, 0, 0, 0);
          const toDate = new Date(dateToFilter);
          toDate.setHours(23, 59, 59, 999);
          return uploadDate >= fromDate && uploadDate <= toDate;
        } else if (dateFromFilter) {
          // Only from date selected
          const fromDate = new Date(dateFromFilter);
          fromDate.setHours(0, 0, 0, 0);
          return uploadDate >= fromDate;
        } else if (dateToFilter) {
          // Only to date selected
          const toDate = new Date(dateToFilter);
          toDate.setHours(23, 59, 59, 999);
          return uploadDate <= toDate;
        }
        return true;
      });
    }

    setFilteredUploads(filtered);
    setCurrentPage(0); // Reset to first page when filters change
  }, [uploads, searchQuery, dateFromFilter, dateToFilter]);

  // Pagination logic
  const totalPages = Math.ceil(filteredUploads.length / rowsPerPage);
  const startIndex = currentPage * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedUploads = filteredUploads.slice(startIndex, endIndex);

  const handleChangePage = (event: unknown, newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const handleDeleteSelected = async () => {
    try {
      const token = sessionStorage.getItem('token');
      
      // Delete each selected upload
      await Promise.all(
        selectedRows.map(uploadId =>
          axios.delete(
            `${getApiUrl()}/api/upload/admin/${uploadId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );

      setSuccessDialogConfig({
        title: 'Uploads Deleted Successfully',
        message: `${selectedRows.length} upload(s) deleted successfully`,
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
      
      setSelectedRows([]);
      setDeleteDialogOpen(false);
      fetchUploads();
    } catch (error: any) {
      setSuccessDialogConfig({
        title: 'Error Deleting Uploads',
        message: error.response?.data?.message || 'Failed to delete uploads',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    }
  };

  const handleDeleteAllForClient = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.delete(
        `${getApiUrl()}/api/upload/admin/client/${selectedClientId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccessDialogConfig({
          title: 'Client Uploads Deleted',
          message: response.data.message,
          autoCloseDelay: 3000,
        });
        setShowSuccessDialog(true);
        setDeleteAllDialogOpen(false);
        fetchUploads();
      }
    } catch (error: any) {
      setSuccessDialogConfig({
        title: 'Error Deleting Client Uploads',
        message: error.response?.data?.message || 'Failed to delete client uploads',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#14BA6D';
      case 'inactive': return '#6C778B';
      default: return '#6C778B';
    }
  };

  const getStatusTextColor = (status: string) => {
    switch (status) {
      case 'active': return '#037847';
      case 'inactive': return '#364254';
      default: return '#364254';
    }
  };

  const getStatusBackgroundColor = (status: string) => {
    switch (status) {
      case 'active': return '#ECFDF3';
      case 'inactive': return '#F2F4F7';
      default: return '#F2F4F7';
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(filteredUploads.map(upload => upload._id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectAllClick = useCallback(() => {
    if (selectedRows.length === filteredUploads.length) {
      // If all are selected, deselect all
      setSelectedRows([]);
    } else {
      // If none or some are selected, select all
      setSelectedRows(filteredUploads.map(upload => upload._id));
    }
  }, [selectedRows.length, filteredUploads]);

  // Force indeterminate state on the checkbox
  useEffect(() => {
    const isIndeterminate = selectedRows.length > 0 && selectedRows.length < filteredUploads.length;
    setShowIndeterminate(isIndeterminate);
  }, [selectedRows.length, filteredUploads.length]);

  const handleSelectRow = useCallback((uploadId: string, checked: boolean) => {
    if (checked) {
      setSelectedRows(prev => [...prev, uploadId]);
    } else {
      setSelectedRows(prev => prev.filter(id => id !== uploadId));
    }
  }, []);

  const clearFilters = () => {
    setSearchQuery('');
    setDateFromFilter(null);
    setDateToFilter(null);
  };

  // Mobile handlers
  const handleExpandRow = (uploadId: string) => {
    setExpandedRow(expandedRow === uploadId ? null : uploadId);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>, upload: UploadRecord) => {
    setMobileMenuAnchor(event.currentTarget);
    setSelectedUploadForMenu(upload);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
    setSelectedUploadForMenu(null);
  };

  const handleMobileDelete = () => {
    if (selectedUploadForMenu) {
      setSelectedRows([selectedUploadForMenu._id]);
      setDeleteDialogOpen(true);
      handleMobileMenuClose();
    }
  };

  const exportData = () => {
    const csvContent = [
      ['Client Name', 'File Name', 'Records', 'Size', 'Version', 'Upload Date', 'Uploaded By'],
      ...filteredUploads.map(upload => [
        upload.clientName,
        upload.fileName,
        upload.recordCount.toString(),
        upload.fileSize,
        `V${upload.version}`,
        formatDate(upload.uploadDate),
        upload.uploadedBy
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `upload-history-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownload = async (uploadId: string, fileName: string, useFiltered: boolean = false) => {
    try {
      const token = sessionStorage.getItem('token');
      const url = `${getApiUrl()}/api/upload/admin/${uploadId}/download${useFiltered ? '?filtered=1' : ''}`;
      const resp = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'download.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error: any) {
      setSuccessDialogConfig({
        title: 'Download Failed',
        message: error.response?.data?.message || 'Could not download file',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    }
  };

  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <>
      {/* Page Header - Outside container */}
      {!isMobile && (
        <Box className="upload-page-header">
          <Typography variant="h2" className={isDarkMode ? 'upload-page-title-dark' : 'upload-page-title-light'}>
            Upload History
          </Typography>
        </Box>
      )}

      {/* Mobile Header */}
      {isMobile && (
        <Box className={`mobile-header ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
          {/* First Row: Title */}
          <Box className={`mobile-header-row ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
            <Typography 
              variant="h5" 
              className={`upload-history-title ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
            >
              Upload History
            </Typography>
          </Box>
          
          {/* Second Row: Search and Reset Filter */}
          <Box className={`mobile-search-row ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
            <TextField
              fullWidth
              placeholder="Search by client, file, or uploader name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ marginRight: 1 }}
            />
            {(searchQuery || dateFromFilter || dateToFilter) && (
              <Button
                variant="text"
                size="small"
                onClick={clearFilters}
                sx={{ 
                  color: '#FF4347',
                  fontWeight: 'bold',
                  minWidth: 'auto',
                  padding: '4px 8px'
                }}
              >
                Reset
              </Button>
            )}
          </Box>
        </Box>
      )}

      <Box className={isDarkMode ? 'upload-history-container-dark' : 'upload-history-container-light'}>

      {/* Desktop Top Bar */}
      {!isMobile && (
      <Box className={isDarkMode ? 'upload-history-top-bar-dark' : 'upload-history-top-bar-light'}>
        {/* Left Side - Action Buttons with Border Radius Container */}
        <Box className={isDarkMode ? 'upload-history-action-buttons-dark' : 'upload-history-action-buttons-light'}>
          {/* Delete - Always visible with bin icon */}
          <Button
            variant="text"
            size="small"
            onClick={() => setDeleteDialogOpen(true)}
            startIcon={
              <svg width="16" height="16" viewBox="0 0 18 16" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M13.5001 15.2012H5.10015C4.4374 15.2012 3.90015 14.6639 3.90015 14.0012V3.20117H14.7001V14.0012C14.7001 14.6639 14.1629 15.2012 13.5001 15.2012Z" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M7.5001 11.6008V6.80078" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M11.1 11.6008V6.80078" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M1.5 3.20117H17.1" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M11.7 3.20117V1.60117C11.7 1.05889 11.2523 0.611172 10.71 0.611172H7.89C7.34772 0.611172 6.9 1.05889 6.9 1.60117V3.20117" stroke="currentColor" strokeWidth="1.2"/>
              </svg>
            }
            disabled={selectedRows.length === 0}
            className={isDarkMode ? 'upload-history-delete-button-dark' : 'upload-history-delete-button-light'}
          >
            Delete
          </Button>

          {/* Filter - Pill Shape */}
          <Button
            variant="text"
            size="small"
            onClick={() => setFilterDialogOpen(true)}
            startIcon={<FilterIcon />}
            className={isDarkMode ? 'upload-history-filter-button-dark' : 'upload-history-filter-button-light'}
          >
            Filter
          </Button>

          {/* Export */}
          <Button
            variant="text"
            size="small"
            onClick={exportData}
            startIcon={
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path d="M6.66667 14.1667L10 17.5001L13.3333 14.1667" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 10.8334V17.5001" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M17.4 15.075C18.1245 14.5655 18.6681 13.8385 18.9562 13.0015C19.2443 12.1644 19.2615 11.2593 19.0056 10.4123C18.7498 9.56524 18.2355 8.8201 17.5311 8.28474C16.8266 7.74937 15.9714 7.45812 15.0917 7.45003H14.0667C13.8126 6.46495 13.3276 5.55488 12.6507 4.79508C11.9737 4.03528 11.1246 3.44924 10.1741 3.08663C9.22359 2.72403 8.19882 2.59653 7.18061 2.71479C6.1624 2.83305 5.18969 3.19378 4.34619 3.7668C3.50269 4.33981 2.81409 5.10977 2.34348 6.00732C1.87286 6.90487 1.63494 7.90481 1.65132 8.91618C1.6677 9.92755 1.93789 10.9201 2.43924 11.8014C2.94058 12.6827 3.65405 13.4267 4.51667 13.9667" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            }
            className={isDarkMode ? 'upload-history-export-button-dark' : 'upload-history-export-button-light'}
          >
            Export
          </Button>

          {/* Clear Filter - Red Button with Restart Icon */}
          <Button
            variant="text"
            color="error"
            size="small"
            onClick={clearFilters}
            startIcon={<RestartIcon />}
            className={isDarkMode ? 'upload-history-clear-filter-button-dark' : 'upload-history-clear-filter-button-light'}
          >
            Reset Filter
          </Button>
        </Box>

        {/* Right Side - Search and Refresh */}
        <Box className={isDarkMode ? 'upload-history-right-container-dark' : 'upload-history-right-container-light'}>
          {/* Search Bar - 70% */}
          <Box className={isDarkMode ? 'upload-history-search-container-dark' : 'upload-history-search-container-light'}>
            <TextField
              fullWidth
              placeholder="Search by client, file, or uploader name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon className={isDarkMode ? 'upload-history-search-icon-dark' : 'upload-history-search-icon-light'} />
                  </InputAdornment>
                ),
              }}
              className={isDarkMode ? 'upload-history-search-input-dark' : 'upload-history-search-input-light'}
            />
          </Box>
          
          {/* Refresh Button - 30% */}
          <Box className={isDarkMode ? 'upload-history-refresh-container-dark' : 'upload-history-refresh-container-light'}>
            <Button
              variant="contained"
              size="small"
              onClick={fetchUploads}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} sx={{ color: '#ffffff' }} /> : <RefreshIcon />}
              className={isDarkMode ? 'upload-history-refresh-button-dark' : 'upload-history-refresh-button-light'}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Box>
      )}

      {/* Desktop Data Table */}
      {!isMobile && (
      <Card className={isDarkMode ? 'upload-history-table-card-dark' : 'upload-history-table-card-light'}>
        <TableContainer sx={{ maxHeight: '70vh' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow className={isDarkMode ? 'upload-history-table-header-dark' : 'upload-history-table-header-light'}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedRows.length === filteredUploads.length && filteredUploads.length > 0}
                    indeterminate={showIndeterminate}
                    onChange={handleSelectAllClick}
                    icon={<UncheckedIcon />}
                    indeterminateIcon={
                      <Box className={isDarkMode ? 'upload-history-checkbox-indeterminate-dark' : 'upload-history-checkbox-indeterminate-light'}>
                        <MinusIcon className={isDarkMode ? 'upload-history-checkbox-icon-dark' : 'upload-history-checkbox-icon-light'} />
                      </Box>
                    }
                    checkedIcon={
                      <Box className={isDarkMode ? 'upload-history-checkbox-checked-dark' : 'upload-history-checkbox-checked-light'}>
                        <MinusIcon className={isDarkMode ? 'upload-history-checkbox-icon-dark' : 'upload-history-checkbox-icon-light'} />
                      </Box>
                    }
                    className={isDarkMode ? 'upload-history-checkbox-dark' : 'upload-history-checkbox-light'}
                    title={`Selected: ${selectedRows.length}, Total: ${filteredUploads.length}, Indeterminate: ${selectedRows.length > 0 && selectedRows.length < filteredUploads.length}`}
                  />
                </TableCell>
                <TableCell sx={{ textAlign: 'left' }}>
                  Client Name
                </TableCell>
                <TableCell sx={{ textAlign: 'left' }}>
                  File Name
                </TableCell>
                <TableCell sx={{ textAlign: 'left' }}>
                  Records
                </TableCell>
                <TableCell sx={{ textAlign: 'left' }}>
                  Size
                </TableCell>
                <TableCell sx={{ textAlign: 'left' }}>
                  Version
                </TableCell>
                <TableCell sx={{ textAlign: 'left' }}>
                  Upload Date
                </TableCell>
                <TableCell sx={{ textAlign: 'left' }}>
                  Uploaded By
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUploads.map((upload, index) => (
                <TableRow
                  key={upload._id}
                  className={`${isDarkMode ? 'upload-history-table-row-dark' : 'upload-history-table-row-light'} ${
                    index % 2 === 0 
                      ? (isDarkMode ? 'upload-history-table-row-even-dark' : 'upload-history-table-row-even-light')
                      : (isDarkMode ? 'upload-history-table-row-odd-dark' : 'upload-history-table-row-odd-light')
                  }`}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedRows.includes(upload._id)}
                      onChange={(e) => handleSelectRow(upload._id, e.target.checked)}
                      icon={<UncheckedIcon />}
                      checkedIcon={
                        <Box className={isDarkMode ? 'upload-history-row-checkbox-checked-dark' : 'upload-history-row-checkbox-checked-light'}>
                          <CheckIcon className={isDarkMode ? 'upload-history-row-checkbox-check-icon-dark' : 'upload-history-row-checkbox-check-icon-light'} />
                        </Box>
                      }
                      className={isDarkMode ? 'upload-history-row-checkbox-dark' : 'upload-history-row-checkbox-light'}
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    <Typography variant="body2" fontWeight="bold" className={`${isDarkMode ? 'upload-history-cell-text-primary-dark' : 'upload-history-cell-text-primary-light'} ${isDarkMode ? 'upload-history-cell-text-bold-dark' : 'upload-history-cell-text-bold-light'}`}>
                      {upload.clientName}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    <Tooltip title="Click to download original file">
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => handleDownload(upload._id, upload.fileName, false)}
                        sx={{ padding: 0, minWidth: 'auto', textTransform: 'none' }}
                        className={isDarkMode ? 'upload-history-cell-text-primary-dark' : 'upload-history-cell-text-primary-light'}
                      >
                        {upload.fileName}
                      </Button>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    <Typography variant="body2" className={`${isDarkMode ? 'upload-history-cell-text-primary-dark' : 'upload-history-cell-text-primary-light'} ${isDarkMode ? 'upload-history-cell-text-bold-dark' : 'upload-history-cell-text-bold-light'}`}>
                      {upload.recordCount.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    <Typography variant="body2" className={isDarkMode ? 'upload-history-cell-text-secondary-dark' : 'upload-history-cell-text-secondary-light'}>
                      {upload.fileSize}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    <Typography variant="body2" className={`${isDarkMode ? 'upload-history-cell-text-primary-dark' : 'upload-history-cell-text-primary-light'} ${isDarkMode ? 'upload-history-cell-text-bold-dark' : 'upload-history-cell-text-bold-light'}`}>
                      V{upload.version}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    <Typography variant="body2" className={isDarkMode ? 'upload-history-cell-text-primary-dark' : 'upload-history-cell-text-primary-light'}>
                      {formatDate(upload.uploadDate)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    <Typography variant="body2" className={isDarkMode ? 'upload-history-cell-text-secondary-dark' : 'upload-history-cell-text-secondary-light'}>
                      {upload.uploadedBy}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <Box className={isDarkMode ? 'upload-history-pagination-container-dark' : 'upload-history-pagination-container-light'}>
          <Typography className={isDarkMode ? 'upload-history-pagination-info-dark' : 'upload-history-pagination-info-light'}>
            Showing {currentPage * rowsPerPage + 1}-{Math.min((currentPage + 1) * rowsPerPage, filteredUploads.length)} of {filteredUploads.length}
          </Typography>
          
          <Box className={isDarkMode ? 'upload-history-pagination-controls-dark' : 'upload-history-pagination-controls-light'}>
            <IconButton
              onClick={() => handleChangePage(null, Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              className={isDarkMode ? 'upload-history-pagination-button-dark' : 'upload-history-pagination-button-light'}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={() => handleChangePage(null, Math.min(totalPages - 1, currentPage + 1))}
              disabled={currentPage >= totalPages - 1}
              className={isDarkMode ? 'upload-history-pagination-button-dark' : 'upload-history-pagination-button-light'}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>
      </Card>
      )}

      {/* Mobile Table */}
      {isMobile && (
        <TableContainer 
          component={Paper} 
          className={`mobile-table-container ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
        >
          <Table className={`mobile-table ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
            <TableHead>
              <TableRow className={`mobile-table-header ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                <TableCell className={`mobile-table-header-cell ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>File</TableCell>
                <TableCell className={`mobile-table-header-cell ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>Status</TableCell>
                <TableCell className={`mobile-table-header-cell ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>Actions</TableCell>
                <TableCell className={`mobile-table-header-cell ${isDarkMode ? 'dark-mode' : 'light-mode'}`}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUploads.map((upload) => (
                <React.Fragment key={upload._id}>
                  {/* Mobile Upload Row */}
                  <TableRow className={`mobile-table-row ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                    {/* Column 1: File Name */}
                    <TableCell className={`mobile-table-cell ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                      <Typography 
                        variant="body2" 
                        className={`table-file-name ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
                      >
                        {upload.fileName}
                      </Typography>
                    </TableCell>
                    
                    {/* Column 2: Status */}
                    <TableCell className={`mobile-table-cell ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                      <Chip
                        label={upload.status}
                        size="small"
                        sx={{
                          backgroundColor: upload.status === 'Completed' ? '#E8F5E8' : '#FFF3E0',
                          color: upload.status === 'Completed' ? '#2E7D32' : '#E65100',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    
                    {/* Column 3: Expand/Collapse */}
                    <TableCell className={`mobile-table-cell ${isDarkMode ? 'dark-mode' : 'light-mode'}`} align="center">
                      <IconButton
                        size="small"
                        className={`expand-collapse-button ${expandedRow === upload._id ? 'expanded' : 'collapsed'} ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
                        onClick={() => handleExpandRow(upload._id)}
                        sx={{
                          backgroundColor: expandedRow === upload._id ? '#FEE2E2' : '#CCF4CD',
                          color: expandedRow === upload._id ? '#DC2626' : '#00C805',
                          '&:hover': {
                            backgroundColor: expandedRow === upload._id ? '#FEE2E2' : '#CCF4CD',
                          },
                          width: 32,
                          height: 32,
                        }}
                      >
                        {expandedRow === upload._id ? (
                          <ExpandLessIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <ExpandMoreIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </TableCell>
                    
                    {/* Column 4: Three Dots Menu */}
                    <TableCell className={`mobile-table-cell ${isDarkMode ? 'dark-mode' : 'light-mode'}`} align="center">
                      <Box
                        onClick={(e) => handleMobileMenuOpen(e, upload)}
                        sx={{ cursor: 'pointer', padding: '8px' }}
                      >
                        <MoreVertIcon 
                          className={`three-dots-icon ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
                          sx={{ fontSize: 20 }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                  
                  {/* Expanded Row Content */}
                  <TableRow>
                    <TableCell colSpan={4} sx={{ padding: 0, border: 0 }}>
                      <Collapse in={expandedRow === upload._id} timeout="auto" unmountOnExit>
                        <Box className={`mobile-upload-expanded ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                          <Box className={`mobile-upload-details ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                            <Box className={`mobile-detail-row ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                              <Typography className={`mobile-detail-label ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                Client:
                              </Typography>
                              <Typography className={`mobile-detail-value ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                {upload.clientName}
                              </Typography>
                            </Box>
                            <Box className={`mobile-detail-row ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                              <Typography className={`mobile-detail-label ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                Records:
                              </Typography>
                              <Typography className={`mobile-detail-value ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                {upload.recordCount.toLocaleString()}
                              </Typography>
                            </Box>
                            <Box className={`mobile-detail-row ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                              <Typography className={`mobile-detail-label ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                Size:
                              </Typography>
                              <Typography className={`mobile-detail-value ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                {upload.fileSize}
                              </Typography>
                            </Box>
                            <Box className={`mobile-detail-row ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                              <Typography className={`mobile-detail-label ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                Version:
                              </Typography>
                              <Typography className={`mobile-detail-value ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                V{upload.version}
                              </Typography>
                            </Box>
                            <Box className={`mobile-detail-row ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                              <Typography className={`mobile-detail-label ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                Upload Date:
                              </Typography>
                              <Typography className={`mobile-detail-value ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                {formatDate(upload.uploadDate)}
                              </Typography>
                            </Box>
                            <Box className={`mobile-detail-row ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                              <Typography className={`mobile-detail-label ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                Uploaded By:
                              </Typography>
                              <Typography className={`mobile-detail-value ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                {upload.uploadedBy}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Mobile Menu */}
      <Menu
        anchorEl={mobileMenuAnchor}
        open={Boolean(mobileMenuAnchor)}
        onClose={handleMobileMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
            border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
          }
        }}
      >
        <MenuItem onClick={handleMobileDelete}>
          <ListItemIcon>
            <DeleteIcon sx={{ color: isDarkMode ? '#ef4444' : '#dc2626' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Delete Upload" 
            sx={{ color: isDarkMode ? '#f1f5f9' : '#1f2937' }}
          />
        </MenuItem>
      </Menu>

      {/* Filter Dialog - Custom Implementation */}
      {filterDialogOpen && (
        <Box className={isDarkMode ? 'upload-history-custom-dialog-overlay-dark' : 'upload-history-custom-dialog-overlay-light'}>
          <Box className={isDarkMode ? 'upload-history-custom-dialog-container-dark' : 'upload-history-custom-dialog-container-light'}>
            {/* Dialog Header */}
            <Box className={isDarkMode ? 'upload-history-custom-dialog-header-dark' : 'upload-history-custom-dialog-header-light'}>
              <FilterIcon className={isDarkMode ? 'upload-history-dialog-icon-dark' : 'upload-history-dialog-icon-light'} />
              <Typography variant="h6" className={isDarkMode ? 'upload-history-dialog-title-text-dark' : 'upload-history-dialog-title-text-light'}>
                Filter Records
              </Typography>
            </Box>
            
            {/* Dialog Content */}
            <Box className={isDarkMode ? 'upload-history-custom-dialog-content-dark' : 'upload-history-custom-dialog-content-light'}>
              <Box className={isDarkMode ? 'upload-history-filter-form-dark' : 'upload-history-filter-form-light'}>
                <Box className={isDarkMode ? 'upload-history-date-group-dark' : 'upload-history-date-group-light'}>
                  <Typography variant="subtitle2" className={isDarkMode ? 'upload-history-filter-label-dark' : 'upload-history-filter-label-light'}>
                    Date Range
                  </Typography>
                  <Box className={isDarkMode ? 'upload-history-date-inputs-container-dark' : 'upload-history-date-inputs-container-light'}>
                    <TextField
                      fullWidth
                      type="date"
                      label="From Date"
                      value={dateFromFilter ? dateFromFilter.toISOString().split('T')[0] : ''}
                      onChange={(e) => {
                        const dateValue = e.target.value;
                        setDateFromFilter(dateValue ? new Date(dateValue) : null);
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      className={isDarkMode ? 'upload-history-modern-date-input-dark' : 'upload-history-modern-date-input-light'}
                    />
                    <TextField
                      fullWidth
                      type="date"
                      label="To Date"
                      value={dateToFilter ? dateToFilter.toISOString().split('T')[0] : ''}
                      onChange={(e) => {
                        const dateValue = e.target.value;
                        setDateToFilter(dateValue ? new Date(dateValue) : null);
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      className={isDarkMode ? 'upload-history-modern-date-input-dark' : 'upload-history-modern-date-input-light'}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
            
            {/* Dialog Actions */}
            <Box className={isDarkMode ? 'upload-history-custom-dialog-actions-dark' : 'upload-history-custom-dialog-actions-light'}>
              <button 
                onClick={() => setFilterDialogOpen(false)}
                className={isDarkMode ? 'upload-history-custom-cancel-button-dark' : 'upload-history-custom-cancel-button-light'}
              >
                Cancel
              </button>
              <button 
                onClick={() => setFilterDialogOpen(false)}
                className={isDarkMode ? 'upload-history-custom-apply-button-dark' : 'upload-history-custom-apply-button-light'}
              >
                Apply Filters
              </button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Delete Selected Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle className={isDarkMode ? 'upload-history-delete-dialog-title-dark' : 'upload-history-delete-dialog-title-light'}>
          Confirm Delete
        </DialogTitle>
        <DialogContent className={isDarkMode ? 'upload-history-dialog-content-dark' : 'upload-history-dialog-content-light'}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography className={isDarkMode ? 'upload-history-alert-text-dark' : 'upload-history-alert-text-light'}>
            Are you sure you want to delete {selectedRows.length} upload(s)?
            This will permanently remove the files and all associated data.
          </Typography>
        </DialogContent>
        <DialogActions className={isDarkMode ? 'upload-history-dialog-actions-dark' : 'upload-history-dialog-actions-light'}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            className={isDarkMode ? 'upload-history-delete-dialog-cancel-button-dark' : 'upload-history-delete-dialog-cancel-button-light'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteSelected} 
            color="error" 
            variant="contained"
            className={isDarkMode ? 'upload-history-delete-dialog-delete-button-dark' : 'upload-history-delete-dialog-delete-button-light'}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete All For Client Dialog */}
      <Dialog open={deleteAllDialogOpen} onClose={() => setDeleteAllDialogOpen(false)}>
        <DialogTitle className={isDarkMode ? 'upload-history-delete-dialog-title-dark' : 'upload-history-delete-dialog-title-light'}>
          Delete All Client Uploads
        </DialogTitle>
        <DialogContent className={isDarkMode ? 'upload-history-dialog-content-dark' : 'upload-history-dialog-content-light'}>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography className={isDarkMode ? 'upload-history-alert-text-dark' : 'upload-history-alert-text-light'}>
            Are you sure you want to delete ALL uploads for client "{selectedClientName}"?
            This will permanently remove all files and data associated with this client.
          </Typography>
        </DialogContent>
        <DialogActions className={isDarkMode ? 'upload-history-dialog-actions-dark' : 'upload-history-dialog-actions-light'}>
          <Button 
            onClick={() => setDeleteAllDialogOpen(false)}
            className={isDarkMode ? 'upload-history-delete-dialog-cancel-button-dark' : 'upload-history-delete-dialog-cancel-button-light'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteAllForClient} 
            color="error" 
            variant="contained"
            className={isDarkMode ? 'upload-history-delete-dialog-delete-button-dark' : 'upload-history-delete-dialog-delete-button-light'}
          >
            Delete All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title={successDialogConfig.title}
        message={successDialogConfig.message}
        buttonText="Got it"
        autoCloseDelay={successDialogConfig.autoCloseDelay}
      />
      </Box>
    </>
  );
};

export default UploadHistory; 